import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';
import { api, ApiError } from '@/lib/api';
import { formatCurrency, formatDateOnly } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Spinner } from '@/components/ui/spinner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PageHeader, BackLink } from '@/layouts/AppLayout';

// ── Types ──────────────────────────────────────────────────────────────────────

type BookingStatus =
  | 'draft'
  | 'tentative'
  | 'confirmed'
  | 'voucher_issued'
  | 'paid'
  | 'completed'
  | 'cancelled'
  | 'released';

interface BookingRoom {
  id: string;
  hotelRoomType: { id: string; name: string; maxOccupancy: number | null };
  mealPlan: string;
  occupancyType: string;
  guestsPerRoom: number;
  roomsCount: number;
  ratePerNight: string;
  rateSource: string;
  rateOverrideReason: string | null;
  amount: string;
  sortOrder: number;
}

interface Booking {
  id: string;
  bookingCode: string;
  guestName: string;
  checkIn: string;
  checkOut: string;
  nights: number | null;
  confNo: string | null;
  status: BookingStatus;
  tentativeUntil: string | null;
  remark: string | null;
  hotel: { id: string; name: string; city: string };
  client: { id: string; name: string };
  bookingRooms: BookingRoom[];
}

const STATUS_LABELS: Record<BookingStatus, string> = {
  draft: 'Draft',
  tentative: 'Tentative',
  confirmed: 'Confirmed',
  voucher_issued: 'Voucher Issued',
  paid: 'Paid',
  completed: 'Completed',
  released: 'Released',
  cancelled: 'Cancelled',
};

// ── Schema ─────────────────────────────────────────────────────────────────────

const schema = z.object({
  guestName: z.string().min(1, 'Guest name is required').max(255),
  confNo: z.string().max(50).optional().or(z.literal('')),
  tentativeUntil: z.string().optional(),
  remark: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

// ── Component ──────────────────────────────────────────────────────────────────

export function EditBookingPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: booking, isLoading } = useQuery({
    queryKey: ['bookings', id],
    queryFn: () => api.get<Booking>(`/bookings/${id}`),
    enabled: !!id,
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    values: booking
      ? {
          guestName: booking.guestName,
          confNo: booking.confNo ?? '',
          tentativeUntil: booking.tentativeUntil
            ? booking.tentativeUntil.slice(0, 10)
            : '',
          remark: booking.remark ?? '',
        }
      : undefined,
  });

  const mutation = useMutation({
    mutationFn: (data: FormValues) => api.patch(`/bookings/${id}`, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['bookings'] });
      toast.success('Booking updated');
      navigate(`/bookings/${id}`);
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Failed to update booking');
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner className="h-6 w-6" />
      </div>
    );
  }
  if (!booking) return <p className="p-6 text-red-600">Booking not found.</p>;

  const nights = booking.nights ?? 0;

  return (
    <div>
      <PageHeader title={`Edit — ${booking.bookingCode}`} />
      <div className="p-6 max-w-3xl space-y-6">
        <BackLink to={`/bookings/${id}`} label="Back to booking" />

        {/* Read-only context */}
        <Card>
          <CardHeader>
            <CardTitle>Booking context</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm sm:grid-cols-3">
              <div>
                <dt className="text-xs text-neutral-500">Hotel</dt>
                <dd className="font-medium">{booking.hotel.name}</dd>
                <dd className="text-xs text-neutral-400">{booking.hotel.city}</dd>
              </div>
              <div>
                <dt className="text-xs text-neutral-500">Client</dt>
                <dd className="font-medium">{booking.client.name}</dd>
              </div>
              <div>
                <dt className="text-xs text-neutral-500">Status</dt>
                <dd className="mt-0.5">
                  <Badge variant="outline">{STATUS_LABELS[booking.status]}</Badge>
                </dd>
              </div>
              <div>
                <dt className="text-xs text-neutral-500">Check-in</dt>
                <dd className="font-medium">{formatDateOnly(booking.checkIn)}</dd>
              </div>
              <div>
                <dt className="text-xs text-neutral-500">Check-out</dt>
                <dd className="font-medium">{formatDateOnly(booking.checkOut)}</dd>
              </div>
              <div>
                <dt className="text-xs text-neutral-500">Nights</dt>
                <dd className="font-medium">{nights}</dd>
              </div>
            </dl>
          </CardContent>
        </Card>

        {/* Read-only rooms */}
        <Card>
          <CardHeader>
            <CardTitle>Rooms</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-3 text-xs text-neutral-400">
              Rooms cannot be changed after creation. To modify rooms, cancel and recreate the booking.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-neutral-100 text-left text-neutral-500">
                    <th className="pb-2 pr-4 font-medium">Room Type</th>
                    <th className="pb-2 pr-4 font-medium">Meal Plan</th>
                    <th className="pb-2 pr-4 font-medium">Occupancy</th>
                    <th className="pb-2 pr-4 font-medium text-right">Rooms</th>
                    <th className="pb-2 pr-4 font-medium text-right">Rate/Night</th>
                    <th className="pb-2 font-medium text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {booking.bookingRooms.map((room) => (
                    <tr key={room.id} className="border-b border-neutral-50">
                      <td className="py-2 pr-4">
                        <span className="font-medium">{room.hotelRoomType.name}</span>
                        {room.rateSource === 'adhoc' && (
                          <span className="ml-1.5 rounded-full bg-orange-100 px-1.5 py-0.5 text-[10px] font-semibold text-orange-700">
                            AD-HOC
                          </span>
                        )}
                      </td>
                      <td className="py-2 pr-4 uppercase">{room.mealPlan}</td>
                      <td className="py-2 pr-4 capitalize">{room.occupancyType}</td>
                      <td className="py-2 pr-4 text-right tabular-nums">{room.roomsCount}</td>
                      <td className="py-2 pr-4 text-right tabular-nums">
                        {formatCurrency(room.ratePerNight)}
                      </td>
                      <td className="py-2 text-right tabular-nums font-medium">
                        {formatCurrency(room.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Editable fields */}
        <form onSubmit={handleSubmit((d) => mutation.mutate(d))}>
          <Card>
            <CardHeader>
              <CardTitle>Booking details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="guestName">Guest name *</Label>
                <Input id="guestName" {...register('guestName')} />
                {errors.guestName && (
                  <p className="text-xs text-red-600">{errors.guestName.message}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="confNo">Confirmation number</Label>
                <Input id="confNo" {...register('confNo')} placeholder="e.g. CONF-12345" />
                {errors.confNo && (
                  <p className="text-xs text-red-600">{errors.confNo.message}</p>
                )}
              </div>

              {booking.status === 'tentative' && (
                <div className="space-y-1.5">
                  <Label htmlFor="tentativeUntil">Tentative until</Label>
                  <Input id="tentativeUntil" type="date" {...register('tentativeUntil')} />
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="remark">Remark</Label>
                <Textarea id="remark" rows={3} {...register('remark')} />
              </div>
            </CardContent>
          </Card>

          <div className="mt-4 flex gap-3">
            <Button type="submit" disabled={isSubmitting || !isDirty}>
              {isSubmitting ? 'Saving…' : 'Save changes'}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(`/bookings/${id}`)}
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
